import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Rocket, CheckCheck, RefreshCw, AlertTriangle, ArrowRight } from "lucide-react";
import { sr } from "../../lib/sr";

export default function GoLiveQueue() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fClient, setFClient] = useState("");
  const [fChannel, setFChannel] = useState("");

  const load = () => {
    setData(null); setSelected([]);
    sr.goLiveQueue().then(setData).catch((e) => {
      if (e?.response?.status === 403) toast.error("Admins only");
      setData({ queue: [], total: 0, ready: 0, blocked: 0 });
    });
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;

  const allRows = data.queue || [];
  const clients = [...new Set(allRows.map((r) => r.tenant_name).filter(Boolean))].sort();
  const channels = [...new Set(allRows.map((r) => r.channel).filter(Boolean))].sort();
  const rows = allRows.filter((r) => (!fClient || r.tenant_name === fClient) && (!fChannel || r.channel === fChannel));
  const readyTaskRows = rows.filter((r) => r.ready && r.source !== "meta");
  const readyMetaRows = rows.filter((r) => r.ready && r.source === "meta");
  const readyRows = rows.filter((r) => r.ready);
  const totalReady = readyRows.length;
  const rid = (r) => r.task_id || r.link_id;
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allReadySelected = readyRows.length > 0 && readyRows.every((r) => selected.includes(rid(r)));
  const toggleAll = () => setSelected(allReadySelected ? [] : readyRows.map(rid));

  const confirm = async (taskIds) => {
    if (!taskIds || !taskIds.length) return;
    setBusy(true);
    try {
      const r = await sr.bulkGoLive(taskIds);
      let msg = `${r.confirmed} campaign(s) are now live`;
      if (r.skipped?.length) msg += ` · ${r.skipped.length} skipped (need review first)`;
      toast.success(msg);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm go-live"); }
    finally { setBusy(false); }
  };

  const approveSelected = async () => {
    const sel = new Set(selected);
    const taskIds = rows.filter((r) => r.source !== "meta" && sel.has(r.task_id)).map((r) => r.task_id);
    const metaIds = rows.filter((r) => r.source === "meta" && sel.has(r.link_id)).map((r) => r.link_id);
    if (!taskIds.length && !metaIds.length) return;
    setBusy(true);
    try {
      let live = 0;
      if (taskIds.length) { const r = await sr.bulkGoLive(taskIds); live += r.confirmed || 0; }
      for (const id of metaIds) { try { await sr.adminMetaGoLive(id); live += 1; } catch { /* keep going */ } }
      toast.success(`${live} campaign(s) are now live`);
      setSelected([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm go-live"); }
    finally { setBusy(false); }
  };

  const goLiveMeta = async (linkId) => {
    setBusy(true);
    try { await sr.adminMetaGoLive(linkId); toast.success("Meta campaign is now live"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not activate on Meta"); }
    finally { setBusy(false); }
  };

  const approveAllReady = async () => {
    if (!totalReady) return;
    setBusy(true);
    try {
      if (readyTaskRows.length) await sr.bulkGoLive(readyTaskRows.map((r) => r.task_id));
      for (const m of readyMetaRows) { try { await sr.adminMetaGoLive(m.link_id); } catch { /* keep going */ } }
      toast.success(`${totalReady} campaign(s) are now live`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm go-live"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="go-live-queue-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Rocket size={22} /> Go-live approvals
          {data.ready > 0 && <span className="sr-badge sr-badge-green" data-testid="go-live-ready-count">{data.ready} ready</span>}
          {data.blocked > 0 && <span className="sr-badge sr-badge-amber" data-testid="go-live-blocked-count">{data.blocked} need review</span>}
        </h1>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="sr-btn sr-btn-sm" onClick={load} data-testid="go-live-refresh"><RefreshCw size={13} /></button>
          <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={approveAllReady} disabled={busy || totalReady === 0} data-testid="go-live-approve-all">
            {busy ? <Loader2 size={13} className="sr-spin" /> : <><Rocket size={14} /> Approve all ready ({totalReady})</>}
          </button>
        </div>
      </div>
      <p className="sr-muted" style={{ marginTop: 6 }}>Every campaign waiting for final go-live — plan launches <strong>and</strong> Meta campaigns published as paused, all in one place. Only an admin can push these live. Items marked <strong>Needs review</strong> must be reviewed by the account manager first.</p>

      {allRows.length > 0 && (clients.length > 1 || channels.length > 1) && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }} data-testid="go-live-filters">
          <span className="sr-muted" style={{ fontSize: 12 }}>Filter:</span>
          <select className="sr-input sr-input-sm" style={{ width: "auto", minWidth: 150 }} value={fClient} onChange={(e) => setFClient(e.target.value)} data-testid="go-live-filter-client">
            <option value="">All clients ({clients.length})</option>
            {clients.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="sr-input sr-input-sm" style={{ width: "auto", minWidth: 130 }} value={fChannel} onChange={(e) => setFChannel(e.target.value)} data-testid="go-live-filter-channel">
            <option value="">All channels ({channels.length})</option>
            {channels.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {(fClient || fChannel) && (
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setFClient(""); setFChannel(""); }} data-testid="go-live-filter-clear">Clear</button>
          )}
          <span className="sr-muted" style={{ fontSize: 12 }}>{rows.length} shown</span>
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13, color: "var(--navy)", cursor: "pointer" }}>
            <input type="checkbox" checked={allReadySelected} onChange={toggleAll} data-testid="go-live-select-all" /> Select all ready
          </label>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={approveSelected} disabled={busy || selected.length === 0} data-testid="go-live-approve-selected">
            {busy ? <Loader2 size={13} className="sr-spin" /> : <><CheckCheck size={14} /> Approve selected ({selected.length})</>}
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="sr-card sr-empty" style={{ marginTop: 14, textAlign: "center", padding: 30 }} data-testid="go-live-empty">
          {allRows.length > 0 ? "No campaigns match this filter." : "No campaigns are waiting for go-live — you're all caught up."}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          {rows.map((r) => {
            const rid = r.task_id || r.link_id;
            const isMeta = r.source === "meta";
            return (
            <div key={rid} className="sr-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }} data-testid={`go-live-row-${rid}`}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: 1 }}>
                <input type="checkbox" style={{ marginTop: 4 }} disabled={!r.ready} checked={selected.includes(rid)} onChange={() => toggle(rid)} data-testid={`go-live-select-${rid}`} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <strong style={{ color: "var(--navy)" }}>{r.tenant_name}</strong>
                    {r.channel && <span className="sr-badge sr-badge-gray">{r.channel}</span>}
                    {r.ready
                      ? <span className="sr-badge sr-badge-green" data-testid={`go-live-status-${rid}`}>{isMeta ? "Awaiting activation" : "Ready to launch"}</span>
                      : <span className="sr-badge sr-badge-amber" data-testid={`go-live-status-${rid}`}><AlertTriangle size={11} /> {r.blocked_reason || "Needs review"}</span>}
                  </div>
                  <div className="sr-muted" style={{ fontSize: 12, marginTop: 3 }}>
                    {r.campaign_name ? `${r.campaign_name} · ` : ""}Status: {r.campaign_status || "—"}{r.budget_monthly ? ` · $${r.budget_monthly}/mo` : ""}
                    {r.plan_id && !isMeta && <> · <Link to={`/app/growth-plan?id=${r.plan_id}`} style={{ color: "var(--blue)" }}>View plan <ArrowRight size={11} /></Link></>}
                  </div>
                </div>
              </div>
              <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => (isMeta ? goLiveMeta(r.link_id) : confirm([r.task_id]))} disabled={busy || !r.ready} data-testid={`go-live-approve-${rid}`}>
                <Rocket size={13} /> Go live
              </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
